import React, { type SyntheticEvent, useState } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import Badge from '@atlaskit/badge';
import Button, { ButtonGroup } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Incident24Icon from '@atlaskit/icon-object/glyph/incident/24';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import LikeIcon from '@atlaskit/icon/glyph/like';
import Modal, {
	ModalTransition,
	ModalHeader,
	ModalTitle,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { LIKE, DISLIKE, DEFAULT_SIMILAR_INCIDENTS_CARD } from '../../../common/constants';
import type { TSimilarIncident } from '../../../common/types';
import LikeFilledIcon from './like-icon-filled';
import messages from './messages';

const getIssueUri = (issueKey: string) => `/browse/${issueKey}`;

export const SimilarIncidentsCard = (props: {
	currentIncident: TSimilarIncident;
	suggestionId: string;
	rank: number;
	key: number;
	defaultExpanded?: boolean;
}) => {
	const [expanded, setExpanded] = useState(Boolean(props.defaultExpanded));
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState('');
	const [hasVoted, setHasVoted] = useState({ hasVotedLike: false, hasVotedDislike: false });
	const affectedServicesFirstThree =
		(props.currentIncident &&
			props.currentIncident.affectedServices &&
			props.currentIncident.affectedServices.slice(0, 3)) ||
		[];
	const analyticsEventData = {
		suggestionId: props.suggestionId,
		rank: props.rank + 1 || 0,
		createdAt: props.currentIncident.createdAt,
		priority: props.currentIncident.priority.id,
		isPirPresent: (props.currentIncident.pir && props.currentIncident.pir.length > 0) || false,
		relevanceScore: props.currentIncident.relevanceScore,
		similarityScore: props.currentIncident.similarityScore,
		affectedServices:
			(props.currentIncident.affectedServices && props.currentIncident.affectedServices.length) ||
			0,
	};

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatDate, formatMessage } = useIntl();

	const closeModal = () => {
		setIsOpen(false);
		fireUIAnalytics(createAnalyticsEvent({}), 'similarIncidentFeedbackModalSubmit clicked', {
			issueId: props.currentIncident.id,
			reason: message,
			...analyticsEventData,
		});
	};

	const expand = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (!expanded)
			fireUIAnalytics(analyticsEvent, 'similarIncidentCardExpanded', {
				issueId: props.currentIncident.id,
				...analyticsEventData,
			});
		setExpanded(!expanded);
	};

	const vote = (input: string, reason?: string) => {
		input === LIKE
			? (setHasVoted({ hasVotedLike: true, hasVotedDislike: false }),
				fireUIAnalytics(createAnalyticsEvent({}), 'similarIncidentPositive clicked', {
					issueId: props.currentIncident.id,
					isHelpful: true,
					...analyticsEventData,
				}))
			: (setHasVoted({ hasVotedLike: false, hasVotedDislike: true }),
				fireUIAnalytics(createAnalyticsEvent({}), 'similarIncidentNegative clicked', {
					issueId: props.currentIncident.id,
					isHelpful: false,
					reason,
					...analyticsEventData,
				}));
	};

	const handleLinkClick = (event: SyntheticEvent, type: string) => {
		fireUIAnalytics(createAnalyticsEvent({}), `similarIncident${type}Link clicked`, {
			issueId: props.currentIncident.id,
			...analyticsEventData,
		});
	};

	const getServiceName = () => {
		const extractServiceNames = props.currentIncident.affectedServices
			.slice(3)
			.map((item) => item.name);
		const tooltipContent = extractServiceNames.join(', ');
		return tooltipContent;
	};

	return (
		<>
			<SimilarIncidentWrapper>
				<SimilarIncidentCard>
					<IncidentIcon>
						<Incident24Icon label="IncidentIcon" />
					</IncidentIcon>
					<IncidentLinkWrapper>
						<KeyButton
							onClick={(e) => handleLinkClick(e, 'HotKey')}
							appearance="link"
							href={getIssueUri(props.currentIncident.key)}
						>
							{props.currentIncident.key}
						</KeyButton>
						<IncidentDateItem>
							{formatDate(props.currentIncident.createdAt, {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
							})}
						</IncidentDateItem>
					</IncidentLinkWrapper>
					<Tooltip content={props.currentIncident.summary}>
						<IncidentTitleSpan> {props.currentIncident.summary} </IncidentTitleSpan>
					</Tooltip>
					<ButtonGroup>
						<IncidentIcon>
							<TypeIcon
								src={
									(props.currentIncident &&
										props.currentIncident.priority &&
										props.currentIncident.priority.iconUrl) ||
									''
								}
							/>
						</IncidentIcon>
						<AvatarSpan>
							<Tooltip
								content={
									(props.currentIncident &&
										props.currentIncident.assignee &&
										props.currentIncident.assignee.displayName) ||
									formatMessage(messages.none)
								}
							>
								<AvatarItem
									avatar={
										<Avatar
											src={
												(props.currentIncident &&
													props.currentIncident.assignee &&
													props.currentIncident.assignee.avatarUrls &&
													props.currentIncident.assignee.avatarUrls['48x48']) ||
												''
											}
										/>
									}
									secondaryText={
										(props.currentIncident &&
											props.currentIncident.assignee &&
											props.currentIncident.assignee.displayName) ||
										formatMessage(messages.none)
									}
								/>
							</Tooltip>
						</AvatarSpan>
					</ButtonGroup>
					<ButtonIcon>
						<ButtonGroup>
							<Button
								onClick={() => {
									vote(LIKE);
								}}
								iconBefore={
									hasVoted.hasVotedLike ? (
										<Tooltip content={formatMessage(messages.helpful)}>
											<LikeFilledIcon label="LikeFilledIcon" size="medium" />
										</Tooltip>
									) : (
										<Tooltip content={formatMessage(messages.helpful)}>
											<LikeIcon label="LikeIcon" />
										</Tooltip>
									)
								}
								appearance="subtle"
								interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.similar-incident-card.button"
							/>
							<DropdownMenu
								trigger={({ triggerRef, ...btnProps }) => (
									<InvertButton
										{...btnProps}
										iconBefore={
											hasVoted.hasVotedDislike ? (
												<Tooltip content={formatMessage(messages.notHelpful)}>
													<LikeFilledIcon label="DislikeFilledIcon" size="medium" />
												</Tooltip>
											) : (
												<Tooltip content={formatMessage(messages.notHelpful)}>
													<LikeIcon label="DislikeIcon" />
												</Tooltip>
											)
										}
										ref={triggerRef}
										appearance="subtle"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem
										onClick={() => {
											vote(DISLIKE, messages.dropdownMessagesfirstItem.defaultMessage);
										}}
									>
										{formatMessage(messages.dropdownMessagesfirstItem)}
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											vote(DISLIKE, messages.dropdownMessageSecondItem.defaultMessage);
										}}
									>
										{formatMessage(messages.dropdownMessageSecondItem)}
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											vote(DISLIKE, messages.dropdownMessagesThirdItem.defaultMessage);
										}}
									>
										{formatMessage(messages.dropdownMessagesThirdItem)}
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											setIsOpen(true);
											vote(DISLIKE, messages.dropdownMessagesFourthItem.defaultMessage);
										}}
									>
										{formatMessage(messages.dropdownMessagesFourthItem)}
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</ButtonGroup>
					</ButtonIcon>
					<IncidentIcon>
						<Button
							iconBefore={<HipchatChevronDownIcon label="DropdownIcon" />}
							appearance="subtle"
							onClick={expand}
							interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.similar-incident-card.button1"
						/>
					</IncidentIcon>
					<ModalTransition>
						{isOpen && (
							<Modal onClose={closeModal} width="400px">
								<ModalHeader>
									<ModalTitle>{formatMessage(messages.dislikeModalTitle)}</ModalTitle>
								</ModalHeader>
								<ModalContentBody>
									<OtherOptionDiv>
										{formatMessage(messages.dropdownMessagesFourthItem)}
										<IncidentIcon>
											<HipchatChevronDownIcon label="dropdownIcon" />
										</IncidentIcon>
									</OtherOptionDiv>
									<TextArea
										resize="none"
										maxHeight="80px"
										placeholder={formatMessage(messages.textAreaPlaceHolder)}
										onChange={(e) => setMessage(e.target.value)}
									/>
								</ModalContentBody>
								<ModalFooter>
									<ButtonGroup>
										<Button
											onClick={closeModal}
											appearance="subtle-link"
											interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.similar-incident-card.button2"
										>
											{formatMessage(messages.dislikeModalCloseButton)}
										</Button>
										<Button
											onClick={closeModal}
											appearance="primary"
											autoFocus
											interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.similar-incident-card.button3"
										>
											{formatMessage(messages.dislikeModalSubmitButton)}
										</Button>
									</ButtonGroup>
								</ModalFooter>
							</Modal>
						)}
					</ModalTransition>
				</SimilarIncidentCard>
				{expanded && (
					<ExtendPanel>
						<ExtendPanelElement>
							<ExtendPanelHeader>{formatMessage(messages.affectedServices)}</ExtendPanelHeader>
							<ExtendPanelItem>
								{affectedServicesFirstThree.length === 0 && (
									<ServiceName>{formatMessage(messages.none)}</ServiceName>
								)}
								{affectedServicesFirstThree.length > 0 &&
									affectedServicesFirstThree.map((item, index) => (
										<ServiceName key={index}>
											{`${affectedServicesFirstThree[index].name},`}&nbsp;
										</ServiceName>
									))}
								{props.currentIncident.affectedServices &&
									props.currentIncident.affectedServices.length > 3 && (
										<Tooltip content={getServiceName}>
											<Badge>{`+${
												props.currentIncident.affectedServices.length -
												DEFAULT_SIMILAR_INCIDENTS_CARD
											} More`}</Badge>
										</Tooltip>
									)}
							</ExtendPanelItem>
						</ExtendPanelElement>
						<ExtendPanelElement>
							<ExtendPanelHeader>{formatMessage(messages.pir)}</ExtendPanelHeader>
							<ExtendPanelItem>
								{(props.currentIncident && props.currentIncident.pir.length && (
									<ViewPIRButton
										appearance="link"
										onClick={(e) => handleLinkClick(e, 'Pir')}
										href={getIssueUri(props.currentIncident.pir[0].key)}
									>
										{formatMessage(messages.viewPir)}
									</ViewPIRButton>
								)) ||
									formatMessage(messages.none)}
							</ExtendPanelItem>
						</ExtendPanelElement>
					</ExtendPanel>
				)}
			</SimilarIncidentWrapper>
		</>
	);
};

export default SimilarIncidentsCard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SimilarIncidentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBottom: token('space.100', '8px'),
	paddingTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottom: `${gridSize / 8}px solid ${token('color.border', colors.N30A)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SimilarIncidentCard = styled.div({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${6 * gridSize}px`,
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncidentIcon = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonIcon = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${10 * gridSize}px`,
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncidentLinkWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${10 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeyButton = styled(Button)({
	margin: '0 0 0 0',
	padding: '0px 0px',
	fontSize: '14px',
	fontWeight: 400,
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingTop: `${gridSize / 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${4 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncidentDateItem = styled.div({
	display: 'flex',
	fontSize: '11px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest', colors.N200)}`,
	marginTop: '0px',
	marginLeft: token('space.025', '2px'),
	fontWeight: 400,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncidentTitleSpan = styled.div({
	fontSize: '14px',
	fontWeight: 400,
	alignItems: 'center',
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${6 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${43 * gridSize}px`,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarSpan = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${17 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `-${gridSize / 2}px`,
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtendPanel = styled.div({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${8 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.accent.gray.subtlest', colors.N20)}`,
	display: 'flex',
	flexDirection: 'row',
	paddingTop: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtendPanelElement = styled.div({
	display: 'flex',
	marginLeft: token('space.100', '8px'),
	flexDirection: 'column',
	padding: `0 ${token('space.100', '8px')} 0 ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtendPanelHeader = styled.div({
	display: 'flex',
	fontSize: '12px',
	fontWeight: 600,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest', colors.N500)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtendPanelItem = styled.div({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	fontSize: '14px',
	fontWeight: 400,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${31 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewPIRButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginTop: `-${gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingTop: `${gridSize / 4}px`,
	paddingLeft: '0px',
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginLeft: `-${gridSize / 8}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 10}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeIcon = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
	marginRight: token('space.025', '2px'),
	verticalAlign: 'middle',
	display: 'inline-flex',
	paddingBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InvertButton = styled(Button)({
	transform: 'scaleY(-1) scaleX(-1)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalContentBody = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 17}px`,
	flexDirection: 'column',
	paddingLeft: token('space.300', '24px'),
	paddingRight: token('space.300', '24px'),
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OtherOptionDiv = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 5}px`,
	// TODO: Use tokens for colors
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	backgroundColor: '#F4F5F7',
	display: 'flex',
	fontSize: '14px',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ServiceName = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${2 * gridSize}px`,
	fontSize: '14px',
});
